/**
 * Plan Selection Step
 *
 * Shows available plans and pricing for enterprise onboarding.
 * Integrates with Stripe for payment processing.
 */

import React, { useState } from 'react';

interface Plan {
  id: string;
  name: string;
  description: string;
  price: number | null; // null = custom pricing
  priceLabel: string;
  interval: 'month' | 'year' | 'custom';
  features: string[];
  highlighted?: boolean;
  ctaLabel: string;
  stripePriceId?: string;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free Assessment',
    description: 'Basic codebase analysis',
    price: 0,
    priceLabel: 'Free',
    interval: 'month',
    features: [
      'Repository structure analysis',
      'Language & framework detection',
      'Basic health score',
      'GitHub Issue report',
      'Limited to 1 repository'
    ],
    ctaLabel: 'Current Plan'
  },
  {
    id: 'starter',
    name: 'Starter',
    description: 'For individual developers',
    price: 29,
    priceLabel: '$29',
    interval: 'month',
    features: [
      'Everything in Free',
      'Up to 5 repositories',
      'Detailed vulnerability scan',
      'Dependency analysis',
      'Architecture recommendations',
      'Email support'
    ],
    ctaLabel: 'Select Starter'
  },
  {
    id: 'professional',
    name: 'Professional',
    description: 'For development teams',
    price: 99,
    priceLabel: '$99',
    interval: 'month',
    highlighted: true,
    features: [
      'Everything in Starter',
      'Unlimited repositories',
      'Team collaboration (up to 10)',
      'AI-powered code generation',
      'Standards enforcement',
      'CI/CD integration',
      'Priority support'
    ],
    ctaLabel: 'Select Professional',
    stripePriceId: 'price_professional_monthly'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Custom solutions for large teams',
    price: null,
    priceLabel: 'Custom',
    interval: 'custom',
    features: [
      'Everything in Professional',
      'Unlimited team members',
      'SSO/SAML integration',
      'Custom agent workflows',
      'On-premise deployment option',
      'Dedicated success manager',
      'SLA guarantees',
      'Custom compliance workflows'
    ],
    ctaLabel: 'Contact Sales'
  }
];

interface PlanSelectionStepProps {
  onPlanSelected: (planId: string) => void;
  selectedPlan?: string;
  assessmentContext?: {
    repository?: string;
    languages?: string[];
    frameworks?: string[];
  };
}

const PlanSelectionStep: React.FC<PlanSelectionStepProps> = ({
  onPlanSelected,
  selectedPlan,
  assessmentContext
}) => {
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');
  const [hoveredPlan, setHoveredPlan] = useState<string | null>(null);

  const getAnnualPrice = (monthlyPrice: number | null): string => {
    if (monthlyPrice === null) return 'Custom';
    if (monthlyPrice === 0) return 'Free';
    const annual = Math.round(monthlyPrice * 10); // 2 months free
    return `$${annual}`;
  };

  const handleSelectPlan = (plan: Plan) => {
    if (plan.id === 'enterprise') {
      // Open contact form or redirect to sales
      window.open('mailto:sales@equilateral.ai?subject=Enterprise%20Plan%20Inquiry', '_blank');
      return;
    }
    onPlanSelected(plan.id);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Choose Your Plan</h2>
        <p className="text-gray-600 mt-1">
          Select the plan that best fits your needs. You can upgrade or downgrade at any time.
        </p>
      </div>

      {/* Assessment Context Banner */}
      {assessmentContext?.repository && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-indigo-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm text-indigo-900 font-medium">
                Based on your assessment of <span className="font-mono">{assessmentContext.repository}</span>
              </p>
              <p className="text-sm text-indigo-700 mt-1">
                We recommend the <strong>Professional</strong> plan for teams working with{' '}
                {assessmentContext.frameworks?.slice(0, 2).join(', ') || 'modern frameworks'}.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Billing Toggle */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-4 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setBillingInterval('month')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              billingInterval === 'month'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval('year')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              billingInterval === 'year'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Annual
            <span className="ml-1 text-xs text-green-600 font-semibold">Save 17%</span>
          </button>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => (
          <div
            key={plan.id}
            onMouseEnter={() => setHoveredPlan(plan.id)}
            onMouseLeave={() => setHoveredPlan(null)}
            className={`relative rounded-xl border-2 p-5 transition-all ${
              selectedPlan === plan.id
                ? 'border-indigo-600 bg-indigo-50'
                : plan.highlighted
                ? 'border-indigo-300 bg-white'
                : 'border-gray-200 bg-white hover:border-gray-300'
            } ${hoveredPlan === plan.id ? 'shadow-lg transform -translate-y-1' : ''}`}
          >
            {/* Popular Badge */}
            {plan.highlighted && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-3 py-1 bg-indigo-600 text-white text-xs font-semibold rounded-full">
                  Most Popular
                </span>
              </div>
            )}

            {/* Plan Header */}
            <div className="text-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
              <p className="text-sm text-gray-500">{plan.description}</p>
            </div>

            {/* Price */}
            <div className="text-center mb-4">
              <div className="text-3xl font-bold text-gray-900">
                {billingInterval === 'year' && plan.price !== null && plan.price > 0
                  ? getAnnualPrice(plan.price)
                  : plan.priceLabel}
              </div>
              {plan.price !== null && plan.price > 0 && (
                <div className="text-sm text-gray-500">
                  per {billingInterval === 'year' ? 'year' : 'month'}
                </div>
              )}
              {plan.price === null && (
                <div className="text-sm text-gray-500">Contact for pricing</div>
              )}
            </div>

            {/* Features */}
            <ul className="space-y-2 mb-6">
              {plan.features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-600">{feature}</span>
                </li>
              ))}
            </ul>

            {/* CTA Button */}
            <button
              onClick={() => handleSelectPlan(plan)}
              disabled={plan.id === 'free'}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-all ${
                plan.id === 'free'
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : selectedPlan === plan.id
                  ? 'bg-indigo-600 text-white'
                  : plan.highlighted
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {selectedPlan === plan.id ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Selected
                </span>
              ) : (
                plan.ctaLabel
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Money-back guarantee */}
      <div className="text-center text-sm text-gray-500">
        <svg className="w-5 h-5 inline-block mr-1 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        30-day money-back guarantee. No questions asked.
      </div>
    </div>
  );
};

export default PlanSelectionStep;
